import React, { useState, useEffect, useRef } from 'react';

export const StatusMetricCard = ({
  label,
  count,
  activeColorClass,
  inactiveColorClass,
  testId,
  onClick,
  containerWidth,
  totalCards,
  gap = 8
}) => {
  const isActive = count > 0;
  const cardRef = useRef(null);
  
  // Smart responsive sizing based on actual container width and number of cards
  const getSmartDimensions = () => {
    if (!containerWidth || !totalCards || containerWidth <= 0) {
      // Fallback to screen-based sizing if container data not available
      const screenWidth = window.innerWidth;
      if (screenWidth < 768) {
        return {
          minWidth: '100px', // Reduced from 120px
          maxWidth: '170px', // Reduced from 200px
          padding: '9px 5px', // Reduced by 10% (was 10px 6px)
          fontSize: '22px', // Increased by 20% (was 18px) - KEEP SAME
          labelFontSize: '12px', // Increased by 20% (was 10px) - KEEP SAME
          marginTop: '3px' // Reduced from 4px
        };
      } else if (screenWidth < 1200) {
        return {
          minWidth: '75px', // Reduced from 90px
          maxWidth: '130px', // Reduced from 150px
          padding: '7px 4px', // Reduced by 10% (was 8px 5px)
          fontSize: '17px', // Increased by 20% (was 14px) - KEEP SAME
          labelFontSize: '10px', // Increased by 20% (was 8px) - KEEP SAME
          marginTop: '2px' // Reduced from 3px
        };
      } else {
        return {
          minWidth: '60px', // Reduced from 70px
          maxWidth: '100px', // Reduced from 120px
          padding: '5px 3px', // Reduced by 10% (was 6px 3px)
          fontSize: '14px', // Increased by 20% (was 12px) - KEEP SAME
          labelFontSize: '8px', // Increased by 20% (was 7px) - KEEP SAME
          marginTop: '1px' // Reduced from 2px
        };
      }
    }

    // Calculate available width per card with safety margin
    const safetyMargin = 10; // Extra pixels to prevent overflow
    const totalGapWidth = (totalCards - 1) * gap;
    const availableWidth = containerWidth - totalGapWidth - safetyMargin;
    const maxCardWidth = Math.floor(availableWidth / totalCards);

    
    // Define size tiers based on available card width (reduced by ~10-15%)
    if (maxCardWidth >= 105) { // Reduced from 120
      // Large cards - plenty of space
      return {
        minWidth: `${Math.min(maxCardWidth, 130)}px`, // Reduced from 150
        maxWidth: `${Math.min(maxCardWidth, 130)}px`, // Reduced from 150
        padding: '9px 5px', // Reduced by 10% (was 10px 6px)
        fontSize: '22px', // Increased by 20% (was 18px) - KEEP SAME
        labelFontSize: '13px', // Increased by 20% (was 11px) - KEEP SAME
        marginTop: '3px' // Reduced from 4px
      };
    } else if (maxCardWidth >= 75) { // Reduced from 90
      // Medium cards
      return {
        minWidth: `${maxCardWidth}px`,
        maxWidth: `${maxCardWidth}px`,
        padding: '7px 4px', // Reduced by 10% (was 8px 5px)
        fontSize: '17px', // Increased by 20% (was 14px) - KEEP SAME
        labelFontSize: '11px', // Increased by 20% (was 9px) - KEEP SAME
        marginTop: '2px' // Reduced from 3px
      };
    } else if (maxCardWidth >= 60) { // Reduced from 70
      // Small cards
      return {
        minWidth: `${maxCardWidth}px`,
        maxWidth: `${maxCardWidth}px`,
        padding: '5px 3px', // Reduced by 10% (was 6px 3px)
        fontSize: '14px', // Increased by 20% (was 12px) - KEEP SAME
        labelFontSize: '10px', // Increased by 20% (was 8px) - KEEP SAME
        marginTop: '1px' // Reduced from 2px
      };
    } else if (maxCardWidth >= 45) { // Reduced from 50
      // Very small cards
      return {
        minWidth: `${maxCardWidth}px`,
        maxWidth: `${maxCardWidth}px`,
        padding: '4px 2px', // Reduced by 10% (was 5px 2px)
        fontSize: '13px', // Increased by 20% (was 11px) - KEEP SAME
        labelFontSize: '8px', // Increased by 20% (was 7px) - KEEP SAME
        marginTop: '1px' // Reduced from 2px
      };
    } else {
      // Ultra compact cards - minimum viable size
      return {
        minWidth: `${Math.max(maxCardWidth, 35)}px`, // Reduced from 40px
        maxWidth: `${Math.max(maxCardWidth, 35)}px`, // Reduced from 40px
        padding: '3px 1px', // Already minimal - keep same
        fontSize: '11px', // Increased by 20% (was 9px) - KEEP SAME
        labelFontSize: '7px', // Increased by 20% (was 6px) - KEEP SAME
        marginTop: '1px'
      };
    }
  };
  
  const dimensions = getSmartDimensions();
  
  // Convert Tailwind-style classes to actual CSS styles
  const getColorStyles = (colorClass) => {
    const styles = {};
    
    if (colorClass.includes('bg-green-600')) { styles.backgroundColor = '#059669'; styles.color = '#ffffff'; }
    else if (colorClass.includes('bg-amber-500')) { styles.backgroundColor = '#f59e0b'; styles.color = '#ffffff'; }
    else if (colorClass.includes('bg-orange-500')) { styles.backgroundColor = '#f97316'; styles.color = '#ffffff'; }
    else if (colorClass.includes('bg-red-600')) { styles.backgroundColor = '#dc2626'; styles.color = '#ffffff'; }
    else if (colorClass.includes('bg-neutral-900')) { styles.backgroundColor = '#171717'; styles.color = '#ffffff'; }
    else if (colorClass.includes('bg-blue-600')) { styles.backgroundColor = '#2563eb'; styles.color = '#ffffff'; }
    else if (colorClass.includes('bg-neutral-500')) { styles.backgroundColor = '#6b7280'; styles.color = '#ffffff'; }
    else if (colorClass.includes('bg-purple-600')) { styles.backgroundColor = '#9333ea'; styles.color = '#ffffff'; }
    else if (colorClass.includes('bg-neutral-100')) { styles.backgroundColor = '#f0f0f0'; styles.color = '#737373'; }
    
    return styles;
  };

  const colorStyles = getColorStyles(isActive ? activeColorClass : inactiveColorClass);

  return (
    <div
      data-testid={testId}
      role="status"
      aria-label={`${label}: ${count}`}
      title={`${label}: ${count}`}
      onClick={onClick}
      style={{
        flex: '0 0 auto', // Don't grow or shrink, use exact calculated size
        width: dimensions.maxWidth,
        minWidth: dimensions.minWidth,
        maxWidth: dimensions.maxWidth,
        minHeight: '40px', // Further reduced by 10% (was 45px)
        borderRadius: '12px',
        padding: dimensions.padding,
        textAlign: 'center', // Horizontal centering
        display: 'flex', // Enable flexbox for vertical centering
        flexDirection: 'column', // Stack number and label vertically
        justifyContent: 'center', // Center content vertically
        alignItems: 'center', // Center content horizontally (backup)
        transition: 'all 0.2s ease',
        cursor: onClick ? 'pointer' : 'default',
        ...colorStyles,
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', // Subtle shadow
        overflow: 'hidden', // Prevent content overflow
        boxSizing: 'border-box', // Include padding/border in width calculations
        wordWrap: 'break-word', // Break long words if necessary
        hyphens: 'auto' // Allow hyphenation for better text fitting
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'scale(1.02)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
        }
      }}
    >
      <div 
        style={{
          fontSize: dimensions.fontSize,
          fontWeight: '600',
          lineHeight: '1',
          margin: '0' // Remove any default margins
        }}
      >
        {count}
      </div>
      <div 
        style={{ 
          fontSize: dimensions.labelFontSize,
          fontWeight: '500', 
          whiteSpace: 'normal', // Allow text wrapping
          overflow: 'hidden', 
          textAlign: 'center', // Center multi-line text
          lineHeight: '1.2', // Better line spacing for multi-line text
          margin: '0', // Remove any default margins
          marginTop: `${Math.max(dimensions.marginTop / 2, 2)}px`, // Reduced spacing for better centering
          wordBreak: 'break-word', // Break long words if needed
          hyphens: 'auto' // Allow hyphenation
        }}
      >
        {label}
      </div>
    </div>
  );
};
